import { useEffect, useRef, useState } from "react";
import { useMap } from "react-map-gl";

import Windy from "./windy";
import { useSelectedWindDirectionSurface } from "../../features/surface/surfaceSlice";
import {
    useIsShowingWindStreamlines,
    useSelectIsShowingWindStreamlinesTuningPanel
} from "../../features/mapDisplay/mapDisplaySlice";
import { useIsMobile } from "../../features/pageWidth/pageWidthSlice";
import { useIsPlaying } from "../../features/time/playbackSlice";
import { useIsRightBarExpanded } from "../../features/Drawer/drawerSlice";
import { useShouldShowAlerts } from "../../features/alertDisplay/alertDisplaySlice";
import { defaultWindParams, WindParamsPanel } from "./WindParamsPanel";

export default function WindyLayer()
{
    const { current: map } = useMap();
    const canvasRef = useRef<HTMLCanvasElement | undefined>( undefined );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const windyRef = useRef<any>( undefined );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const timeoutRef = useRef<any>( undefined );
    const cleanupFns = useRef<Array<() => void>>( [] );
    const isMobile = useIsMobile();
    const shouldShowWindStreamlinesTuningPanel = useSelectIsShowingWindStreamlinesTuningPanel();

    const [ windParams, setWindParams ] = useState( defaultWindParams );

    const windDirectionSurface = useSelectedWindDirectionSurface();
    const isPlaying = useIsPlaying();
    const isShowingWindStreamlines = useIsShowingWindStreamlines();
    const isShowingAlerts = useShouldShowAlerts();
    const shouldShowWindStreamlines = isShowingWindStreamlines && !isPlaying && !isShowingAlerts;
    const expandedRightBar = useIsRightBarExpanded();

    const getParticleWidth = ( zoom: number ) =>
    {
        const m = windParams.particleLineWidthMultiplier;

        const minZoom = 0;
        const maxZoom = 13;

        const minWidth = 0.002;
        const maxWidth = 0.45;

        const t = Math.min( Math.max( ( maxZoom - zoom ) / ( maxZoom - minZoom ), 0 ), 1 );

        const width = minWidth * Math.pow( maxWidth / minWidth, t );

        return width * m;
    };

    useEffect( () =>
    {
        if ( !map ) return;

        const cleanup = () =>
        {
            if ( canvasRef.current )
            {
                canvasRef.current.remove();
                canvasRef.current = undefined;
            }
            if ( windyRef.current !== undefined )
            {
                windyRef.current.stop();
            }
            if ( timeoutRef.current !== undefined )
            {
                clearTimeout( timeoutRef.current );
                timeoutRef.current = undefined;
            }
            // Remove all stored listeners
            cleanupFns.current.forEach( ( fn ) => fn() );
            cleanupFns.current = [];
        };

        if ( windDirectionSurface?.url === undefined || !shouldShowWindStreamlines )
        {
            cleanup();
            return;
        }

        const controller = new AbortController();
        const signal = controller.signal;
        const mapContainer = map.getContainer();

        const fetchWindData = async () =>
        {
            const response = await fetch( windDirectionSurface.url, { signal } );
            return response.json();
        };

        const initCanvas = () =>
        {
            cleanup(); // Remove old canvas if exists
            const canvas = document.createElement( "canvas" );
            Object.assign( canvas.style, {
                position: "absolute",
                top: "0",
                left: "0",
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                zIndex: "0",
            } );
            mapContainer.appendChild( canvas );
            canvasRef.current = canvas;
            return canvas;
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const initWindy = ( canvas: HTMLCanvasElement, windData: any ) =>
        {
            if ( windyRef.current === undefined )
            {
                windyRef.current = Windy( { canvas, data: windData, isMobile, windParams } );
            }
            else
            {
                windyRef.current.update( canvas, windData, windParams );
            }
        };

        const setupMapListeners = ( resetWind: () => void ) =>
        {
            const events = [ "move", "zoom", "resize" ] as const;
            events.forEach( event => map.on( event, resetWind ) );
            cleanupFns.current.push( () => events.forEach( event => map.off( event, resetWind ) ) );
        };

        const resetWind = () =>
        {
            if ( canvasRef.current === undefined || map === undefined ) return;

            //This function does a lot of bookeeping to ensure that the canvas size matches the map, as the canvas is drawn
            // on top of the map. If the canvas size is wrong, we can end up with fuzzy streamlines or broken binning
            // when the browser zom changes.
            const canvas = canvasRef.current;
            //this bounding box gives the map width and height in LOGICAL/CSS pixels, which accounts for browser zoom
            const boundingBox = map.getContainer().getBoundingClientRect();
            const width = boundingBox.width;
            const height = boundingBox.height;
            const bounds = map.getBounds();
            const zoom = map.getZoom();
            const dpr = window.devicePixelRatio || 1; // device pixel ratio

            canvas.style.display = "none";
            windyRef.current?.stop();
            if ( timeoutRef.current !== undefined ) clearTimeout( timeoutRef.current );

            timeoutRef.current = globalThis.setTimeout( () =>
            {
                //modify the physical size of the canvas to ensure it has enough pixels to render the streamlines
                canvas.width = width * dpr;
                canvas.height = height * dpr;

                // Keep logical/css pixel size consistent with the map's logical pixel size
                canvas.style.width = `${width}px`;
                canvas.style.height = `${height}px`;

                // Scale drawing context so coordinates match CSS pixels, rather than physical pixels
                const ctx = canvas.getContext( "2d" );
                if ( ctx ) ctx.setTransform( dpr, 0, 0, dpr, 0, 0 );

                canvas.style.display = "block";

                windyRef.current?.start(
                    [ [ 0, 0 ], [ width, height ] ],
                    width,
                    height,
                    [
                        [ bounds.getWest(), bounds.getSouth() ],
                        [ bounds.getEast(), bounds.getNorth() ],
                    ],
                    { particleLineWidth: getParticleWidth( zoom ) }
                );
            }, 500 );
        };

        const initialize = async () =>
        {
            try
            {
                const windData = await fetchWindData();
                const canvas = initCanvas();
                initWindy( canvas, windData );
                setupMapListeners( resetWind );
                resetWind();
            }
            catch ( error )
            {
                if ( ( error as Error ).name !== "AbortError" )
                {
                    console.error( "Failed to fetch or render wind data:", error );
                }
            }
        };

        void initialize();

        return () =>
        {
            cleanup();
            controller.abort();
        };
    }, [
        map,
        windDirectionSurface?.url,
        shouldShowWindStreamlines,
        windParams,
        isMobile
    ] );

    if ( !shouldShowWindStreamlinesTuningPanel )
    {
        return null;
    }

    return (
        <WindParamsPanel
            windParams = {windParams}
            setWindParams = {setWindParams}
            expandedRightBar = {expandedRightBar}
        />
    );
}
